import { msg } from "@lingui/core/macro";
import { Bell, CalendarClock, Repeat, X } from "lucide-solid";
import { createEffect, createSignal, type JSX, onCleanup, Show, untrack } from "solid-js";
import { cn, splitLocalDateTime } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog } from "@/components/ui/dialog";
import { createUsesSheet } from "@/lib/use-media-query";
import { SchedulePanel, type ScheduleValue } from "./schedule-panel";
import { Button, buttonVariants } from "@/components/ui/button";

interface ScheduleFieldProps {
  value: () => ScheduleValue;
  onChange: (patch: Partial<ScheduleValue>) => void;
  open: () => boolean;
  setOpen: (open: boolean) => void;
  shortcut?: string;
  shortcutTitle?: string;
}

function dueLabel(value: ScheduleValue, locale: string, fallback: string): string {
  if (!value.due_date) {
    return fallback;
  }
  const { time } = splitLocalDateTime(value.due_date);
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    ...(time ? { hour: "numeric", minute: "2-digit" } : {}),
  }).format(new Date(value.due_date));
}

/**
 * Unified Google-Calendar-style scheduling control: one row summarising the due
 * date, reminder and recurrence, opening an adaptive surface — an anchored
 * popover on desktop, a touch-friendly bottom sheet on mobile/touch.
 */
const DEFAULT_MAX_HEIGHT_PX = 680;
const MIN_POPOVER_HEIGHT_PX = 200;
const SCREEN_BOTTOM_PADDING_PX = 24;

export function ScheduleField(props: ScheduleFieldProps) {
  const { t, locale } = useI18n();
  const usesSheet = createUsesSheet();

  let triggerRef: HTMLButtonElement | undefined = undefined;
  const [availableHeight, setAvailableHeight] = createSignal(DEFAULT_MAX_HEIGHT_PX);
  const [draft, setDraft] = createSignal<ScheduleValue>(untrack(() => props.value()));

  const handleSave = () => {
    console.log("[ScheduleField] handleSave: saving draft to DB");
    props.onChange(draft());
    props.setOpen(false);
  };

  const handleCancel = () => {
    console.log("[ScheduleField] handleCancel: discarding draft");
    props.setOpen(false);
  };

  const handleOpenChange = (openState: boolean) => {
    console.log("[ScheduleField] handleOpenChange: openState =", openState);
    if (openState) {
      setDraft(props.value());
      props.setOpen(true);
    } else {
      props.setOpen(false);
    }
  };

  createEffect(() => {
    const el = triggerRef;
    if (props.open() && el) {
      const updateHeight = () => {
        const rect = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - SCREEN_BOTTOM_PADDING_PX;
        setAvailableHeight(Math.max(MIN_POPOVER_HEIGHT_PX, spaceBelow));
      };

      updateHeight();

      window.addEventListener("resize", updateHeight);
      window.addEventListener("scroll", updateHeight, true);
      onCleanup(() => {
        window.removeEventListener("resize", updateHeight);
        window.removeEventListener("scroll", updateHeight, true);
      });
    }
  });

  const hasValue = () => {
    const v = props.value();
    return Boolean(v.due_date ?? v.reminder_at ?? v.recurrence_rule);
  };

  const getOverlayClass = () => {
    if (hasValue()) {
      return "bg-primary/10 group-active/btn:bg-primary/20";
    }
    return "bg-foreground/5 group-active/btn:bg-foreground/10";
  };

  const getKbdClass = () => {
    if (hasValue()) {
      return "border-border/40 bg-background text-foreground/90 group-hover/btn:border-primary/40 group-hover/btn:text-primary";
    }
    return "border-primary/20 bg-primary/5 text-primary group-hover/btn:border-primary/40 group-hover/btn:bg-primary/10";
  };

  const triggerContent = (): JSX.Element => (
    <>
      <div
        class={cn(
          "absolute inset-0 opacity-0 group-hover/btn:opacity-100 group-active/btn:opacity-100 transition-all duration-200 pointer-events-none",
          getOverlayClass(),
        )}
      />
      <span class={cn("flex w-full items-center gap-3 z-10", hasValue() ? "pr-6" : "")}>
        <CalendarClock
          class={cn(
            "h-4.5 w-4.5 shrink-0 transition-transform duration-200",
            hasValue() &&
              "fill-current scale-110 drop-shadow-[0_0_8px_rgba(var(--accent-rgb),0.3)]",
          )}
        />
        <span class="truncate">
          {dueLabel(props.value(), locale(), t(msg`Add date & reminder`))}
        </span>
        <span class="ml-auto flex items-center gap-1.5 mr-1 z-10">
          <Show when={props.value().reminder_at}>
            <Bell class="h-3.5 w-3.5 text-primary" />
          </Show>
          <Show when={props.value().recurrence_rule}>
            <Repeat class="h-3.5 w-3.5 text-primary" />
          </Show>
          <Show when={props.shortcut && !hasValue()}>
            <kbd
              class={cn(
                "h-4.5 min-w-[15px] inline-flex items-center justify-center rounded px-1.5 font-mono text-[9px] font-bold transition-all duration-200 opacity-0 group-hover/btn:opacity-100 group-focus-within/btn:opacity-100 group-focus/btn:opacity-100 select-none cursor-help border",
                getKbdClass(),
              )}
              title={props.shortcutTitle}
            >
              {props.shortcut}
            </kbd>
          </Show>
        </span>
      </span>
    </>
  );

  const clearButton = (): JSX.Element => (
    <Show when={hasValue()}>
      <button
        type="button"
        aria-label={t(msg`Clear date & reminder`)}
        class="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-primary/20 text-primary cursor-pointer active:scale-90 transition-all flex items-center justify-center shrink-0 z-10"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          props.onChange({ due_date: null, reminder_at: null, recurrence_rule: null });
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <X class="h-3.5 w-3.5" />
      </button>
    </Show>
  );

  const triggerClass = (withBaseVariants = false) =>
    cn(
      withBaseVariants && buttonVariants({ variant: "ghost" }),
      "w-full justify-start gap-3 h-11 px-3 rounded-lg transition-all duration-200 group/btn relative overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer select-none",
      hasValue() ? "text-primary bg-primary/10" : "hover:bg-muted/10 text-foreground/80",
    );

  return (
    <Show
      when={usesSheet()}
      fallback={
        <div class="relative w-full">
          <Popover open={props.open()} onOpenChange={handleOpenChange} placement="bottom-start">
            <PopoverTrigger
              ref={(el) => {
                triggerRef = el;
              }}
              as="button"
              type="button"
              class={triggerClass(true)}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {triggerContent()}
            </PopoverTrigger>
            <PopoverContent class="w-[340px] z-[150] p-0 overflow-hidden">
              <div
                class="overflow-y-auto p-4"
                style={{ "max-height": `${Math.min(DEFAULT_MAX_HEIGHT_PX, availableHeight())}px` }}
              >
                <SchedulePanel
                  value={draft}
                  onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              </div>
            </PopoverContent>
          </Popover>
          {clearButton()}
        </div>
      }
    >
      <div class="relative w-full">
        <Button
          type="button"
          variant="ghost"
          class={triggerClass(false)}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            setDraft(props.value());
            props.setOpen(true);
          }}
        >
          {triggerContent()}
        </Button>
        {clearButton()}
      </div>
      <Dialog open={props.open()} onOpenChange={handleOpenChange} contentClass="p-4">
        <div class="max-h-[80vh] overflow-y-auto px-1 pb-1">
          <SchedulePanel
            value={draft}
            onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
      </Dialog>
    </Show>
  );
}
